/**
 * Présentation des états non nominaux d'une requête (MON-16) :
 * - chargement sans donnée → squelettes (jamais les anciennes données) ;
 * - 403 de périmètre → message dédié, les filtres restent utilisables ;
 * - 403 de rôle → message de rôle ;
 * - erreur (500, réseau, 400) → message, données effacées.
 * Rend `null` quand les données sont là : l'écran les affiche.
 */
import React from 'react';
import { Alert, Button, Card, Col, Row, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import type { QueryState } from './useMonitoringQuery';
import { cardStyle } from '../monitoringTheme';
import { ERROR_CODES } from './i18nCatalog';

export const LoadingSkeleton: React.FC<{ tiles?: number }> = ({ tiles = 6 }) => (
    <div data-testid="monitoring-skeleton" aria-busy="true">
        <Row gutter={[12, 12]}>
            {Array.from({ length: tiles }, (_, i) => (
                <Col key={i} xs={12} sm={8} lg={4}>
                    <Card size="small" style={cardStyle}>
                        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />
                    </Card>
                </Col>
            ))}
        </Row>
        <Card style={{ ...cardStyle, marginTop: 16 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
    </div>
);


const QueryStatusView: React.FC<{
    query: QueryState<unknown> & { reload: () => void };
    tiles?: number;
}> = ({ query, tiles }) => {
    const { t } = useTranslation();
    switch (query.status) {
        case 'idle':
        case 'success':
        case 'refreshing':
            return null;
        case 'loading':
            return <LoadingSkeleton tiles={tiles} />;
        case 'forbidden_scope':
            return (
                <Alert
                    type="warning"
                    showIcon
                    data-testid="forbidden-scope"
                    message={t('monitoring.errors.scopeForbidden')}
                    description={t('monitoring.errors.scopeForbiddenHelp')}
                />
            );
        case 'role_required':
            return <Alert type="error" showIcon data-testid="role-required" message={t('monitoring.errors.roleRequired')} />;
        case 'error':
        default: {
            const code = query.error?.code;
            const detail = code && (ERROR_CODES as readonly string[]).includes(code) ? t(`monitoring.errors.code.${code}`) : null;
            return (
                <Alert
                    type="error"
                    showIcon
                    data-testid="query-error"
                    message={t('monitoring.errors.load')}
                    description={detail}
                    action={
                        <Button size="small" onClick={query.reload}>
                            {t('monitoring.filters.retry')}
                        </Button>
                    }
                />
            );
        }
    }
};

export default QueryStatusView;
